import i18n from "@/lib/i18n";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { 
  Send, Loader2, Eraser, LifeBuoy, X, 
  MessageCircle, Bell, ArrowLeft, AlertCircle, ShieldAlert, Calendar, User, CheckCircle,
  Trash2
} from "lucide-react";
import { BeraAvatar } from "./BeraAvatar";
import { useBeraAI } from "@/hooks/useBeraAI";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn, getFirstImageUrl } from "@/lib/utils";
import { analyzeMessageForSafety, maskSensitive } from "@/lib/beraAiSanitize";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";

import { tAuto } from "@/lib/tAuto";
const QUICK_KEYS = [
  { key: "beraAI.quick1", fallback: "Como faço uma reserva?" },
  { key: "beraAI.quick2", fallback: "Como cancelar uma reserva?" },
  { key: "beraAI.quick3", fallback: "Quais os métodos de pagamento?" },
  { key: "beraAI.quick4", fallback: "Quero registar uma situação" },
];

export const BeraAIChat = ({ 
  onClose, 
  onHeaderPointerDown,
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
  selectedBookingChat: propSelectedBookingChat,
  setSelectedBookingChat: propSetSelectedBookingChat
}: { 
  onClose?: () => void;
  onHeaderPointerDown?: (e: React.PointerEvent) => void;
  activeTab?: "ai" | "conversas" | "alertas";
  setActiveTab?: (tab: "ai" | "conversas" | "alertas") => void;
  selectedBookingChat?: any | null;
  setSelectedBookingChat?: (chat: any | null) => void;
}) => {
  const { t } = useTranslation();
  const { messages, isStreaming, send, clear, escalate, error } = useBeraAI();
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isGuest, isHost, isSuperAdmin, isAdmin } = useUserRoles();

  // Tabs states
  const [localActiveTab, setLocalActiveTab] = useState<"ai" | "conversas" | "alertas">("ai");
  const [localSelectedBookingChat, setLocalSelectedBookingChat] = useState<any | null>(null);

  const activeTab = propActiveTab !== undefined ? propActiveTab : localActiveTab;
  const setActiveTab = propSetActiveTab !== undefined ? propSetActiveTab : setLocalActiveTab;
  const selectedBookingChat = propSelectedBookingChat !== undefined ? propSelectedBookingChat : localSelectedBookingChat;
  const setSelectedBookingChat = propSetSelectedBookingChat !== undefined ? propSetSelectedBookingChat : setLocalSelectedBookingChat;

  // Confirmation state removed as per user request (direct deletions)

  // Booking chat states
  const [bookingMessages, setBookingMessages] = useState<any[]>([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [bookingInput, setBookingInput] = useState("");
  const [safetyWarning, setSafetyWarning] = useState("");
  const bookingMessagesEndRef = useRef<HTMLDivElement>(null);

  // Determine role access
  const hasConversations = !!user && (isGuest || isHost || isSuperAdmin || isAdmin);

  // 1. Fetch conversations (bookings + other party + last message + unread count)
  const { data: conversations = [], isLoading: isConversationsLoading } = useQuery({
    queryKey: ["bera-ai-conversations", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("bookings")
        .select(`
          id,
          status,
          check_in,
          check_out,
          guest_id,
          host_id,
          properties (
            title,
            images
          )
        `)
        .or(`guest_id.eq.${user.id},host_id.eq.${user.id}`)
        .order("created_at", { ascending: false });
        
      if (error) throw error;

      // Carrega conversas escondidas pelo utilizador
      const { data: hiddenRows } = await (supabase as any)
        .from("chat_conversation_hidden")
        .select("booking_id, created_at")
        .eq("user_id", user.id);
      const hiddenMap = new Map<string, string>(
        (hiddenRows || []).map((r: any) => [r.booking_id, r.created_at])
      );
      
      const chats = await Promise.all(
        (data || []).map(async (b: any) => {
          const otherPartyId = b.guest_id === user.id ? b.host_id : b.guest_id;
          
          const { data: profile } = await (supabase as any)
            .from("public_profiles")
            .select("full_name, avatar_url")
            .eq("id", otherPartyId)
            .maybeSingle();
            
          const { data: lastMsgs } = await supabase
            .from("chat_messages")
            .select("*")
            .eq("booking_id", b.id)
            .order("created_at", { ascending: false })
            .limit(1);
            
          const { count } = await supabase
            .from("chat_messages")
            .select("*", { count: "exact", head: true })
            .eq("booking_id", b.id)
            .eq("receiver_id", user.id)
            .eq("is_read", false);
            
          return {
            booking: b,
            otherProfile: profile || { full_name: "Utilizador BeraStays", avatar_url: null },
            lastMessage: lastMsgs?.[0] || null,
            unreadCount: count || 0,
            otherPartyId
          };
        })
      );

      // Filtra conversas escondidas (reaparecem se houver mensagem nova posterior)
      const visibleChats = chats.filter((c) => {
        const hiddenAt = hiddenMap.get(c.booking.id);
        if (!hiddenAt) return true;
        if (!c.lastMessage) return false;
        return new Date(c.lastMessage.created_at).getTime() > new Date(hiddenAt).getTime();
      });
      
      return visibleChats.sort((a, b) => {
        const timeA = a.lastMessage ? new Date(a.lastMessage.created_at).getTime() : 0;
        const timeB = b.lastMessage ? new Date(b.lastMessage.created_at).getTime() : 0;
        return timeB - timeA;
      });
    },
    enabled: !!user?.id && hasConversations,
  });

  // 2. Fetch notifications list
  const { data: notifications = [], isLoading: isNotificationsLoading, refetch: refetchNotifications } = useQuery({
    queryKey: ["bera-ai-notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("push_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && hasConversations,
  });

  // Count unread
  const totalUnreadMessages = conversations.reduce((acc, c) => acc + c.unreadCount, 0);
  const totalUnreadNotifications = notifications.filter(n => !n.is_read).length;

  // Realtime subscription for list & badges
  useEffect(() => {
    if (!user?.id || !hasConversations) return;
    
    const channel = supabase
      .channel("bera-ai-conversations-realtime")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "chat_messages"
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["bera-ai-conversations", user.id] });
      })
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "push_notifications",
        filter: `user_id=eq.${user.id}`
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["bera-ai-notifications", user.id] });
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, hasConversations, queryClient]);

  // Scroll to bottom for Bera AI Assistant messages
  useEffect(() => {
    if (activeTab === "ai") {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTab]);

  // Fetch and subscribe to selected conversation messages
  const fetchBookingMessages = async (bookingId: string) => {
    setIsMessagesLoading(true);
    const { data } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("booking_id", bookingId)
      .order("created_at", { ascending: true });
    setBookingMessages(data || []);
    setIsMessagesLoading(false);
    
    // Mark as read
    if (user?.id && data && data.length > 0) {
      const unreadIds = data
        .filter(m => m.receiver_id === user.id && !m.is_read)
        .map(m => m.id);
      if (unreadIds.length > 0) {
        await supabase.from("chat_messages").update({ is_read: true }).in("id", unreadIds);
        queryClient.invalidateQueries({ queryKey: ["bera-ai-conversations", user.id] });
      }
    }
  };

  useEffect(() => {
    if (!selectedBookingChat) return;
    
    fetchBookingMessages(selectedBookingChat.booking.id);
    setSafetyWarning("");

    const channel = supabase
      .channel(`widget-chat-room-${selectedBookingChat.booking.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
        filter: `booking_id=eq.${selectedBookingChat.booking.id}`,
      }, (payload) => {
        setBookingMessages(prev => {
          if (prev.some(m => m.id === payload.new.id)) return prev;
          return [...prev, payload.new];
        });
        
        // Mark as read if received
        if (payload.new.receiver_id === user?.id) {
          supabase.from("chat_messages").update({ is_read: true }).eq("id", payload.new.id).then(() => {
            queryClient.invalidateQueries({ queryKey: ["bera-ai-conversations", user?.id] });
          });
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBookingChat, user?.id]);

  // Scroll to bottom for booking chat
  useEffect(() => {
    if (selectedBookingChat) {
      bookingMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [bookingMessages, selectedBookingChat]);

  // Actions
  const handleSendAI = (text?: string) => {
    const textContent = (text ?? input).trim();
    if (!textContent) return;
    
    const safety = analyzeMessageForSafety(textContent);
    if (!safety.isSafe) {
      toast({
        title: "Mensagem Bloqueada",
        description: safety.reason,
        variant: "destructive",
      });
      return;
    }
    
    setInput("");
    send(textContent);
  };

  const handleSendBooking = async () => {
    const text = bookingInput.trim();
    if (!text || !selectedBookingChat || !user?.id) return;
    
    // Safety check
    const safety = analyzeMessageForSafety(text);
    if (!safety.isSafe) {
      const warningText = safety.reason || "";
      setSafetyWarning(warningText);
      toast({
        title: i18n.t("components.securityAlert") || "Alerta de Segurança",
        description: warningText,
        variant: "destructive"
      });

      // Log to audit / chat_reports
      try {
        const masked = maskSensitive(text);
        await supabase.from("chat_reports").insert({
          booking_id: selectedBookingChat.booking.id,
          reporter_id: user.id,
          reported_user_id: user.id,
          report_type: "mensagem_bloqueada",
          message: `[Bloqueado] Tentativa de envio de mensagem insegura no Widget. Conteúdo mascarado: "${masked}"`,
          status: "pending"
        });
      } catch (err) {
        console.error("Error logging blocked message:", err);
      }
      return;
    }

    setBookingInput("");
    setSafetyWarning("");

    try {
      // 1. Insert message
      await supabase.from("chat_messages").insert({
        booking_id: selectedBookingChat.booking.id,
        sender_id: user.id,
        receiver_id: selectedBookingChat.otherPartyId,
        message: text,
        is_ai_response: false,
      });

      // 2. Insert notification
      await supabase.from("push_notifications").insert({
        user_id: selectedBookingChat.otherPartyId,
        title: i18n.t("components.newMessageFrom", { name: user.user_metadata?.full_name || "BeraStays" }) || `Nova mensagem de ${user.user_metadata?.full_name || "BeraStays"}`,
        message: text.length > 60 ? `${text.slice(0, 60)}...` : text,
        type: "new_chat_message",
        data: { booking_id: selectedBookingChat.booking.id, sender_id: user.id },
      });

      // 3. Trigger AI auto-reply if guest
      const isUserHost = user.id === selectedBookingChat.booking.host_id;
      if (!isUserHost) {
        supabase.functions.invoke("chat-ai-reply", {
          body: { 
            booking_id: selectedBookingChat.booking.id, 
            message: text, 
            guest_name: user.user_metadata?.full_name || "Hóspede", 
            property_title: selectedBookingChat.booking.properties?.title || "", 
            check_in: selectedBookingChat.booking.check_in, 
            check_out: selectedBookingChat.booking.check_out 
          },
        }).catch(() => {});
      }

      // Local refresh
      fetchBookingMessages(selectedBookingChat.booking.id);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: i18n.t("components.sendError") || "Erro ao enviar",
        description: err.message || "Não foi possível enviar a mensagem."
      });
    }
  };

  const handleEscalate = () => {
    const summary = messages
      .slice(-6)
      .map((m) => `${m.role === "user" ? "U" : "B"}: ${m.content}`)
      .join("\n");
    escalate("geral", summary);
  };

  const markNotification = async (notifId: string) => {
    await supabase.from("push_notifications").update({ is_read: true }).eq("id", notifId);
    refetchNotifications();
  };

  const handleDeleteNotification = async (id: string) => {
    // Optimistic: remove from cache immediately
    const key = ["bera-ai-notifications", user?.id];
    const previous = queryClient.getQueryData<any[]>(key);
    queryClient.setQueryData<any[]>(key, (prev) => (prev || []).filter((n) => n.id !== id));
    try {
      const { error } = await supabase.from("push_notifications").delete().eq("id", id);
      if (error) throw error;
    } catch (err: any) {
      queryClient.setQueryData(key, previous);
      toast({
        variant: "destructive",
        title: "Erro ao eliminar",
        description: err.message || "Ocorreu um erro ao processar a ação."
      });
    }
  };

  const handleClearAllNotifications = async () => {
    const key = ["bera-ai-notifications", user?.id];
    const previous = queryClient.getQueryData<any[]>(key);
    queryClient.setQueryData<any[]>(key, []);
    try {
      const { error } = await supabase.from("push_notifications").delete().eq("user_id", user?.id);
      if (error) throw error;
    } catch (err: any) {
      queryClient.setQueryData(key, previous);
      toast({
        variant: "destructive",
        title: "Erro ao eliminar",
        description: err.message || "Ocorreu um erro ao processar a ação."
      });
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    // Optimistic remove from local booking messages
    setBookingMessages((prev) => prev.filter((m) => m.id !== messageId));
    try {
      const { error } = await supabase.rpc("hide_chat_message_for_user" as any, { p_message_id: messageId });
      if (error) throw error;
    } catch (err: any) {
      if (selectedBookingChat) fetchBookingMessages(selectedBookingChat.booking.id);
      toast({
        variant: "destructive",
        title: "Erro ao eliminar",
        description: err.message || "Ocorreu um erro ao processar a ação."
      });
    }
  };

  const handleClearConversation = async (bookingId: string) => {
    const key = ["bera-ai-conversations", user?.id];
    const previous = queryClient.getQueryData<any[]>(key);
    queryClient.setQueryData<any[]>(key, (prev) => (prev || []).filter((c: any) => c?.booking?.id !== bookingId));
    if (selectedBookingChat && selectedBookingChat.booking.id === bookingId) {
      setSelectedBookingChat(null);
    }
    try {
      const { error } = await supabase.rpc("hide_booking_chat_for_user" as any, { p_booking_id: bookingId });
      if (error) throw error;
    } catch (err: any) {
      queryClient.setQueryData(key, previous);
      toast({
        variant: "destructive",
        title: "Erro ao eliminar",
        description: err.message || "Ocorreu um erro ao processar a ação."
      });
    }
  };

  const handleClearAllConversations = async () => {
    const key = ["bera-ai-conversations", user?.id];
    const previous = queryClient.getQueryData<any[]>(key);
    queryClient.setQueryData<any[]>(key, []);
    setSelectedBookingChat(null);
    try {
      const { error } = await supabase.rpc("hide_all_booking_chats_for_user" as any);
      if (error) throw error;
      toast({ title: "Conversas eliminadas" });
    } catch (err: any) {
      queryClient.setQueryData(key, previous);
      toast({
        variant: "destructive",
        title: "Erro ao eliminar",
        description: err.message || "Ocorreu um erro ao processar a ação."
      });
    }
  };



  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: "Pendente",
      awaiting_confirmation: "Pendente",
      awaiting_payment: "Aguardando Pagto",
      confirmed: "Confirmada",
      cancelled: "Cancelada",
      completed: "Concluída",
    };
    return map[status] || status;
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div 
        className={cn(
          "flex items-center gap-3 border-b border-border px-4 py-3 select-none flex-shrink-0",
          onHeaderPointerDown && "cursor-grab active:cursor-grabbing"
        )}
        onPointerDown={onHeaderPointerDown}
        style={{ touchAction: "none" }}
      >
        <BeraAvatar size="md" showStatus />
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-tight flex items-center gap-1.5">
            <span>Bera</span>
            {activeTab === "conversas" && <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full font-bold">{t("beraAI.chats") || "Chats"}</span>}
            {activeTab === "alertas" && <span className="text-[10px] text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full font-bold">{t("beraAI.alerts") || "Alertas"}</span>}
          </div>
          <div className="text-xs text-muted-foreground">{t("beraAI.subtitle") || "Assistente & Central inteligente"}</div>
        </div>
        <div className="flex items-center gap-1">
          {activeTab === "ai" && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clear} onPointerDown={(e) => e.stopPropagation()} title={t("beraAI.clearConversation") || "Limpar conversa"}>
              <Eraser className="h-4 w-4" />
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} onPointerDown={(e) => e.stopPropagation()} title={t("beraAI.close") || "Fechar"}>
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {hasConversations && !selectedBookingChat && (
        <div className="flex border-b border-border bg-muted/20 flex-shrink-0">
          <button
            onClick={() => setActiveTab("ai")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center justify-center gap-1.5",
              activeTab === "ai"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <img src="/bera-ia-avatar.png" alt="Bera" className={`rounded-full object-cover ${"h-3.5 w-3.5"}`} />
            Bera
          </button>
          <button
            onClick={() => setActiveTab("conversas")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center justify-center gap-1.5 relative",
              activeTab === "conversas"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Conversas
            {totalUnreadMessages > 0 && (
              <span className="absolute top-1.5 right-2 bg-red-500 text-white text-[8px] font-bold min-w-[14px] h-3.5 rounded-full flex items-center justify-center px-1">
                {totalUnreadMessages}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("alertas")}
            className={cn(
              "flex-1 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center justify-center gap-1.5 relative",
              activeTab === "alertas"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Bell className="h-3.5 w-3.5" />
            Alertas
            {totalUnreadNotifications > 0 && (
              <span className="absolute top-1.5 right-3 bg-red-500 text-white text-[8px] font-bold min-w-[14px] h-3.5 rounded-full flex items-center justify-center px-1">
                {totalUnreadNotifications}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col relative">

        {/* ── TAB: ASSISTENTE BERA IA ── */}
        {activeTab === "ai" && (
          <div className="flex-1 flex flex-col overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center text-center py-6 gap-3">
                <BeraAvatar size="xl" />
                <div>
                  <h3 className="font-semibold text-lg">{t("beraAI.hello") || "Olá, sou a Bera"}</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xs leading-relaxed">
                    Assistente BeraStays. Estou aqui para te ajudar com reservas, pagamentos, propriedades e mais.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-2 w-full max-w-sm pt-2">
                  {QUICK_KEYS.map(({ key, fallback }) => {
                    const q = t(key) || fallback;
                    return (
                    <button
                      key={key}
                      onClick={() => handleSendAI(q)}
                      className="text-left text-xs px-3.5 py-2.5 rounded-xl border border-border hover:bg-muted/50 transition font-medium"
                    >
                      {q}
                    </button>
                    );
                  })}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  "flex gap-2",
                  m.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                {m.role === "assistant" && <BeraAvatar size="sm" />}
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed",
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-muted text-foreground rounded-bl-md",
                  )}
                >
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 text-xs">
                      {m.content ? (
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
        )}

        {/* ── TAB: CONVERSAS (LIST) ── */}
        {activeTab === "conversas" && !selectedBookingChat && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {conversations.length > 0 && !isConversationsLoading && (
              <div className="flex justify-between items-center px-4 py-2 border-b border-border bg-muted/10 shrink-0">
                <span className="text-[10px] font-bold text-muted-foreground uppercase">
                  {conversations.length} {conversations.length === 1 ? "Conversa" : "Conversas"}
                </span>
                <button
                  onClick={() => handleClearAllConversations()}
                  className="text-[10px] text-red-500 hover:text-red-700 font-bold flex items-center gap-1 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Limpar tudo
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isConversationsLoading ? (
              <div className="flex flex-col justify-center items-center py-12 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="text-xs text-muted-foreground">{t("beraAI.loadingConversations") || "A carregar conversas..."}</span>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-xs font-semibold text-muted-foreground">{t("beraAI.noActiveChats") || "Nenhum bate-papo ativo"}</p>
                <p className="text-[10px] text-muted-foreground/80 mt-1 max-w-[200px] mx-auto leading-relaxed">
                  As tuas conversas surgirão aqui automaticamente assim que fizeres uma reserva ou proposta.
                </p>
              </div>
            ) : (
              conversations.map((c: any) => {
                const img = getFirstImageUrl(c.booking.properties?.images);
                const lastMsgText = c.lastMessage?.message || t("beraAI.startConversation") || "Inicia a conversa...";
                
                return (
                  <div
                    key={c.booking.id}
                    onClick={() => setSelectedBookingChat(c)}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl bg-muted/30 border border-border/30 hover:bg-muted/70 transition text-left relative cursor-pointer group"
                  >
                    {/* Thumbnail of Property */}
                    <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 bg-muted relative">
                      {img ? <img src={img} alt="Property" className="w-full h-full object-cover" /> : <Calendar className="w-5 h-5 text-muted-foreground m-auto" />}
                    </div>

                    {/* Chat details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-bold text-xs text-foreground truncate">{c.otherProfile.full_name}</span>
                        <span className="text-[9px] text-muted-foreground shrink-0">
                          {c.lastMessage ? format(new Date(c.lastMessage.created_at), "HH:mm") : ""}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate mb-1">{c.booking.properties?.title}</p>
                      <p className="text-[11px] text-foreground/80 truncate font-medium">{lastMsgText}</p>
                    </div>

                    {/* Unread & Status badges */}
                    <div className="flex flex-col items-end gap-1.5 shrink-0 ml-1">
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[8px] font-extrabold uppercase bg-primary/10 text-primary px-1.5 py-0.5 rounded-md">
                          {getStatusBadge(c.booking.status)}
                        </span>
                        <button
                          onClick={() => {
                            handleClearConversation(c.booking.id);
                          }}
                          className="text-muted-foreground hover:text-red-500 p-1 rounded-md hover:bg-red-500/10 transition"
                          title={t("beraAI.deleteConversation") || "Apagar conversa"}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {c.unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shrink-0">
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </div>
        )}

        {/* ── SUB-SCREEN: DETALHE DO CHAT DE RESERVA ── */}
        {selectedBookingChat && (
          <div className="absolute inset-0 bg-background z-20 flex flex-col overflow-hidden">
            {/* Sub-Header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => { setSelectedBookingChat(null); queryClient.invalidateQueries({ queryKey: ["bera-ai-conversations", user?.id] }); }}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-xs text-foreground leading-tight truncate">
                  {selectedBookingChat.otherProfile.full_name}
                </p>
                <p className="text-[9px] text-muted-foreground truncate">
                  {selectedBookingChat.booking.properties?.title}
                </p>
              </div>
              <span className="text-[8px] font-extrabold uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-md shrink-0">
                {getStatusBadge(selectedBookingChat.booking.status)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-red-500 rounded-full shrink-0"
                onClick={() => {
                  handleClearConversation(selectedBookingChat.booking.id);
                }}
                title={t("beraAI.clearConversation") || "Limpar conversa"}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Checkin Info Banner */}
            <div className="bg-[#E8513D]/5 px-4 py-1.5 border-b border-[#E8513D]/10 text-[9px] font-semibold text-zinc-500 dark:text-zinc-400 flex justify-between shrink-0">
              <span>Reserva #{selectedBookingChat.booking.id.slice(0, 8).toUpperCase()}</span>
              <span>{format(new Date(selectedBookingChat.booking.check_in), "dd MMM")} - {format(new Date(selectedBookingChat.booking.check_out), "dd MMM yyyy")}</span>
            </div>

            {/* Messages list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {isMessagesLoading ? (
                <div className="flex justify-center items-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </div>
              ) : bookingMessages.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">{t("beraAI.noMessagesInChat") || "Nenhuma mensagem nesta conversa."}</p>
                  <p className="text-[10px] mt-0.5">{t("beraAI.sendToStart") || "Envia uma mensagem abaixo para iniciar."}</p>
                </div>
              ) : (
                bookingMessages.map((msg: any) => {
                  const isMine = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={cn("flex items-center gap-2 group", isMine ? "flex-row-reverse" : "flex-row")}>
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-3.5 py-2 relative text-xs leading-relaxed",
                          isMine
                            ? "bg-primary text-white rounded-br-sm"
                            : "bg-muted text-foreground rounded-bl-sm"
                        )}
                      >
                        {msg.is_ai_response && (
                          <span className="inline-flex items-center gap-1 mb-0.5 text-[8px] font-bold uppercase text-primary/70 bg-primary/10 px-1 py-0.25 rounded">
                            Auto-Reply
                          </span>
                        )}
                        <p className="whitespace-pre-wrap">{msg.message}</p>
                        <p className={cn("text-[8px] text-right mt-1", isMine ? "text-white/60" : "text-muted-foreground/60")}>
                          {format(new Date(msg.created_at), "HH:mm")}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          handleDeleteMessage(msg.id);
                        }}
                        className={cn(
                          "text-muted-foreground hover:text-red-500 p-1 rounded-md hover:bg-muted/50 transition duration-200 shrink-0",
                          "opacity-40 md:opacity-0 group-hover:opacity-100 focus:opacity-100"
                        )}
                        title="Apagar mensagem"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
              <div ref={bookingMessagesEndRef} />
            </div>

            {/* Safety Warning Panel */}
            {safetyWarning && (
              <div className="bg-destructive/10 border-t border-destructive/20 p-3 flex gap-2 items-start text-xs text-destructive animate-fade-in shrink-0">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-[11px]">{tAuto("auto.components_bera_ai_beraaichat.1", "Regras de Segurança da BeraStays")}</p>
                  <p className="mt-0.5 leading-relaxed text-[10px]">{safetyWarning}</p>
                </div>
                <button onClick={() => setSafetyWarning("")} className="text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Sub-Input Area */}
            <div className="p-3 border-t bg-background shrink-0">
              <div className="flex items-end gap-2">
                <Textarea
                  value={bookingInput}
                  onChange={(e) => setBookingInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendBooking();
                    }
                  }}
                  placeholder="Escreve a tua mensagem..."
                  rows={1}
                  className="min-h-[44px] max-h-24 resize-none rounded-2xl text-xs py-3"
                />
                <Button
                  onClick={handleSendBooking}
                  disabled={!bookingInput.trim()}
                  size="icon"
                  className="h-11 w-11 rounded-2xl shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: ALERTAS & NOTIFICAÇÕES ── */}
        {activeTab === "alertas" && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {notifications.length > 0 && !isNotificationsLoading && (
              <div className="flex justify-between items-center px-4 py-2 border-b border-border bg-muted/10 shrink-0">
                <span className="text-[10px] font-bold text-muted-foreground uppercase">
                  {notifications.length} {notifications.length === 1 ? "Alerta" : "Alertas"}
                </span>
                <button
                  onClick={() => handleClearAllNotifications()}
                  className="text-[10px] text-red-500 hover:text-red-700 font-bold flex items-center gap-1 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Limpar tudo
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {isNotificationsLoading ? (
                <div className="flex flex-col justify-center items-center py-12 gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="text-xs text-muted-foreground">A carregar alertas...</span>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-xs font-semibold text-muted-foreground">{tAuto("auto.components_bera_ai_beraaichat.2", "Sem notificações")}</p>
                  <p className="text-[10px] text-muted-foreground/80 mt-1 max-w-[180px] mx-auto leading-relaxed">
                    Não tens notificações ou alertas importantes de momento.
                  </p>
                </div>
              ) : (
                notifications.map((n: any) => (
                  <div
                    key={n.id}
                    onClick={() => markNotification(n.id)}
                    className={cn(
                      "w-full p-3 rounded-2xl border text-left flex gap-3 relative transition cursor-pointer group",
                      n.is_read 
                        ? "bg-muted/10 border-border/20" 
                        : "bg-primary/5 border-primary/20 hover:bg-primary/10"
                    )}
                  >
                    {/* Icon Indicator */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      n.is_read ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
                    )}>
                      <Bell className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={cn("text-xs font-bold leading-snug", n.is_read ? "text-foreground/80" : "text-foreground")}>
                        {n.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">{n.message}</p>
                      <span className="text-[8px] text-muted-foreground/80 mt-1 block">
                        {format(new Date(n.created_at), "dd MMM yyyy, HH:mm")}
                      </span>
                    </div>

                    <div className="flex flex-col justify-between items-end shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          handleDeleteNotification(n.id);
                        }}
                        className="text-muted-foreground hover:text-red-500 p-1 rounded-md hover:bg-red-500/10 transition"
                        title="Eliminar alerta"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {!n.is_read && (
                        <span className="w-2.5 h-2.5 bg-primary rounded-full shrink-0 animate-pulse mt-2" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Registar caso interno / Escalonar */}
      {activeTab === "ai" && (
        <div className="px-4 pb-2 shrink-0 border-t border-border/30 pt-1">
          <button
            onClick={handleEscalate}
            className="w-full flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground hover:text-primary transition py-1.5 font-semibold"
            title={tAuto("auto.components_bera_ai_beraaichat.3", "Registar esta conversa para análise interna da equipa BeraStays")}
          >
            <LifeBuoy className="h-3.5 w-3.5" />
            Registar caso para análise interna
          </button>
        </div>
      )}

      {/* Input de IA (apenas visível se na aba assistente) */}
      {activeTab === "ai" && (
        <div className="border-t border-border p-3 bg-background shrink-0">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendAI();
                }
              }}
              placeholder="Escreve a tua mensagem..."
              rows={1}
              className="min-h-[44px] max-h-32 resize-none rounded-2xl text-xs py-3"
              disabled={isStreaming}
            />
            <Button
              onClick={() => handleSendAI()}
              disabled={isStreaming || !input.trim()}
              size="icon"
              className="h-11 w-11 rounded-2xl shrink-0"
            >
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          {error && <p className="text-[10px] text-destructive mt-1">{error}</p>}
          <p className="text-[9px] text-muted-foreground mt-1.5 text-center leading-normal">
            Não partilhes dados bancários ou palavras-passe.
          </p>
        </div>
      )}

    </div>
  );
};

export default BeraAIChat;
